import React, { useState, useEffect, useRef, useMemo } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Loader2, X, ShieldCheck, Eraser, PenLine, Type, FolderOpen, Download } from 'lucide-react';
import { openAuthed } from '../lib/authSession';
import { TRANSACTION_TYPES } from './ComplianceChecklistModal';

const META_FIELDS = [
  ['broker', 'Broker'], ['velocity_expert_no', 'Velocity / Expert No'],
  ['co_broker', 'Co-Broker'], ['client_names', 'Client/s Name/s *'],
  ['co_broker_firm', 'Co-Broker Firm'], ['property_address', 'Property Address'],
  ['co_broker_address', 'Co-Broker Address'], ['closing_date', 'Closing Date'],
  ['lender', 'Lender'], ['transaction_type', 'Type of Transaction'],
];

const EMPTY_FORM = {
  broker: '', co_broker: '', co_broker_firm: '', co_broker_address: '', lender: '', transaction_type: '',
  velocity_expert_no: '', client_names: '', property_address: '', closing_date: '',
  referral_source: '', referred_by: '', signed_name: '',
  signature_date: new Date().toISOString().slice(0, 10),
  outcome: 'pending', review_notes: '', fintrac_notes: '',
  fintrac_report_filed: false, fintrac_report_type: '', fintrac_report_date: '',
  folder_id: null, package_file_id: null,
};

const inputCls = 'w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]';

/**
 * DealComplianceReviewModal — compliance-officer review form that replicates
 * the File Compliance Checklist. Filled manually by admin; optionally linked to
 * the Final Compliance Package from an agent's client folder (review queue).
 */
const DealComplianceReviewModal = ({ apiUrl, adminPassword, reviewId, queueItem, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outcomes, setOutcomes] = useState([]);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [items, setItems] = useState([]);
  const [sigMode, setSigMode] = useState('draw');
  const [sigTyped, setSigTyped] = useState('');
  const sigPadRef = useRef(null);
  const headers = { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [optRes, rvRes] = await Promise.all([
          fetch(`${apiUrl}/api/admin/deal-compliance/items`, { headers }),
          reviewId ? fetch(`${apiUrl}/api/admin/deal-compliance/reviews/${reviewId}`, { headers }) : Promise.resolve(null),
        ]);
        if (!alive || !optRes.ok) return;
        const opts = await optRes.json();
        const rv = rvRes && rvRes.ok ? await rvRes.json() : null;
        setOutcomes(opts.outcomes || []);
        setExisting(rv);
        const prevItems = new Map((rv?.items || []).map(it => [it.key, it]));
        setItems((opts.items || []).map(it => ({
          key: it.key, label: it.label, section: it.section || 'Other',
          checked: !!prevItems.get(it.key)?.checked,
        })));
        if (rv) {
          setForm(prev => ({
            ...prev,
            ...Object.fromEntries(Object.keys(prev).filter(k => rv[k] != null).map(k => [k, rv[k]])),
            signature_date: new Date().toISOString().slice(0, 10),
          }));
          if (rv.signature_typed) { setSigMode('type'); setSigTyped(rv.signature_typed); }
        } else if (queueItem) {
          setForm(prev => ({ ...prev, client_names: queueItem.client_name || '', folder_id: queueItem.folder_id, package_file_id: queueItem.package_file_id }));
        }
      } catch (e) {
        console.error('Failed to load deal review:', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiUrl, reviewId]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleItem = (key) => setItems(prev => prev.map(it => it.key === key ? { ...it, checked: !it.checked } : it));
  const tickedCount = useMemo(() => items.filter(i => i.checked).length, [items]);
  const linked = form.folder_id ? {
    agent_name: queueItem?.agent_name || existing?.agent_name,
    client_name: queueItem?.client_name || existing?.client_names,
    package_file_id: queueItem?.package_file_id || form.package_file_id,
    filename: queueItem?.filename,
  } : null;

  const groups = useMemo(() => {
    const out = [];
    items.forEach(it => {
      const last = out[out.length - 1];
      if (!last || last.section !== it.section) out.push({ section: it.section, items: [it] });
      else last.items.push(it);
    });
    return out;
  }, [items]);

  const handleSubmit = async () => {
    if (!form.client_names?.trim()) { alert('Please enter the Client/s Name/s.'); return; }
    let signature_drawn_png = null;
    if (sigMode === 'draw') {
      const pad = sigPadRef.current;
      if (pad?.isEmpty?.() ?? true) { alert('Please sign in the box (or switch to Type mode).'); return; }
      try { signature_drawn_png = pad.getTrimmedCanvas().toDataURL('image/png'); }
      catch { signature_drawn_png = pad.getCanvas().toDataURL('image/png'); }
    }
    const signature_typed = sigMode === 'type' ? (sigTyped || '').trim() : null;
    if (sigMode === 'type' && !signature_typed) { alert('Please type your signature.'); return; }
    if (!form.signed_name?.trim()) { alert("Please enter the compliance officer's printed name."); return; }

    setSaving(true);
    try {
      const url = reviewId ? `${apiUrl}/api/admin/deal-compliance/reviews/${reviewId}` : `${apiUrl}/api/admin/deal-compliance/reviews`;
      const r = await fetch(url, {
        method: reviewId ? 'PUT' : 'POST', headers,
        body: JSON.stringify({
          ...form,
          fintrac_report_type: form.fintrac_report_type || null,
          fintrac_report_date: form.fintrac_report_date || null,
          items: items.map(it => ({ key: it.key, label: it.label, checked: it.checked })),
          signature_typed, signature_drawn_png,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Could not save review.'); }
      const data = await r.json();
      onSaved?.(data.review);
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="deal-review-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl my-8 overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#0B3D7A] to-[#0077B6] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold">{existing ? `Edit Deal Compliance Review — Revision ${existing.revision + 1}` : 'New Deal Compliance Review'}</h2>
              <p className="text-xs text-white/80">{existing ? `Client: ${existing.client_names}` : linked ? `Reviewing the Final Compliance Package from ${linked.agent_name ? `${linked.agent_name}'s` : 'the agent\'s'} client folder — fill in every field manually.` : 'Fill in every field manually — nothing is pre-filled from agent folders.'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="deal-review-close-btn"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 max-h-[80vh] overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
          ) : (
            <>
              {linked && (
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4 p-3 rounded-lg border border-orange-200 bg-orange-50" data-testid="deal-review-linked-banner">
                  <div className="text-xs text-orange-900">
                    <p className="font-bold flex items-center gap-1"><FolderOpen className="h-3.5 w-3.5" /> Linked to agent client folder</p>
                    <p>Client: <b>{linked.client_name}</b>{linked.agent_name ? <> • Agent: <b>{linked.agent_name}</b></> : null}{linked.filename ? <> • {linked.filename}</> : null}</p>
                    <p className="text-orange-700">Saving this review marks the package as <b>Reviewed</b> in the For Review queue.</p>
                  </div>
                  {linked.package_file_id && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`${apiUrl}/api/client-files/download/${linked.package_file_id}`} onClick={openAuthed} target="_blank" rel="noopener noreferrer" data-testid="deal-review-open-package">
                        <Download className="h-4 w-4 mr-1" />Open Final Package
                      </a>
                    </Button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {META_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                    {k === 'transaction_type' ? (
                      <select value={form[k] || ''} onChange={(e) => set(k, e.target.value)} className={`${inputCls} bg-blue-50/30`} data-testid="deal-review-field-transaction_type">
                        <option value="">Select…</option>
                        {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    ) : (
                    <input type={k === 'closing_date' ? 'date' : 'text'} value={form[k] || ''} onChange={(e) => set(k, e.target.value)}
                      className={`${inputCls} bg-blue-50/30`} data-testid={`deal-review-field-${k}`} />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-800">Documents Checklist</h3>
                <span className="text-xs text-gray-500" data-testid="deal-review-ticked-count">{tickedCount} of {items.length} ticked • only ticked items print</span>
              </div>
              <div className="border rounded-lg overflow-hidden mb-5">
                {groups.map(g => (
                  <div key={g.section}>
                    <div className="px-3 py-1.5 bg-[#EFF3FA] text-[#0B3D7A] text-xs font-bold uppercase tracking-wide border-y border-[#D7DCE6]">{g.section}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                      {g.items.map(it => (
                        <label key={it.key} className={`flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${it.checked ? 'bg-emerald-50/60' : ''}`} data-testid={`deal-review-item-${it.key}`}>
                          <input type="checkbox" checked={it.checked} onChange={() => toggleItem(it.key)} className="mt-0.5 h-4 w-4 accent-emerald-600" />
                          <span className="text-sm text-gray-800">{it.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Referral Source</label>
                  <input type="text" value={form.referral_source} onChange={(e) => set('referral_source', e.target.value)} className={inputCls} data-testid="deal-review-field-referral_source" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Referred by</label>
                  <input type="text" value={form.referred_by} onChange={(e) => set('referred_by', e.target.value)} className={inputCls} data-testid="deal-review-field-referred_by" />
                </div>
              </div>

              <div className="border rounded-lg p-3 mb-5 space-y-3 bg-amber-50/30 border-amber-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Review Outcome</label>
                    <select value={form.outcome} onChange={(e) => set('outcome', e.target.value)} className={inputCls} data-testid="deal-review-outcome">
                      {outcomes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-800 pb-1.5">
                      <input type="checkbox" checked={form.fintrac_report_filed} onChange={(e) => set('fintrac_report_filed', e.target.checked)} className="h-4 w-4" data-testid="deal-review-fintrac-filed" />
                      FINTRAC report filed
                    </label>
                  </div>
                </div>
                {form.fintrac_report_filed && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Report Type</label>
                      <select value={form.fintrac_report_type || ''} onChange={(e) => set('fintrac_report_type', e.target.value)} className={inputCls} data-testid="deal-review-fintrac-type">
                        <option value="">Select…</option>
                        <option value="STR">STR — Suspicious Transaction Report</option>
                        <option value="LCTR">LCTR — Large Cash Transaction Report</option>
                        <option value="TPR">TPR — Terrorist Property Report</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Report Date</label>
                      <input type="date" value={form.fintrac_report_date || ''} onChange={(e) => set('fintrac_report_date', e.target.value)} className={inputCls} data-testid="deal-review-fintrac-date" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Compliance Officer Notes</label>
                  <textarea rows={3} value={form.review_notes || ''} onChange={(e) => set('review_notes', e.target.value)} className={inputCls} placeholder="Findings, deficiencies, follow-ups…" data-testid="deal-review-notes" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900 mb-1">FINTRAC Reporting Notes</label>
                  <textarea rows={4} value={form.fintrac_notes || ''} onChange={(e) => set('fintrac_notes', e.target.value)} className={`${inputCls} border-amber-300 bg-white`} placeholder="PEP/HIO determinations, third-party determination, source of funds, large cash, suspicious indicators, reports filed with FINTRAC…" data-testid="deal-review-fintrac-notes" />
                </div>
              </div>

              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 leading-relaxed mb-5">
                I, the undersigned Compliance Officer, confirm that I have independently reviewed the compliance package for this deal,
                verified that each document ticked above is present and complete, assessed the file against FINTRAC / AML obligations and
                Utmost Financial Inc. compliance policy, and recorded my findings and any required reporting in the notes above.
              </div>

              <div className="border rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold text-gray-800">Compliance Officer Signature</label>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1">
                    <button type="button" onClick={() => setSigMode('draw')} className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${sigMode === 'draw' ? 'bg-white shadow text-[#0077B6]' : 'text-gray-600'}`} data-testid="deal-review-sig-draw"><PenLine className="h-3 w-3" /> Draw</button>
                    <button type="button" onClick={() => setSigMode('type')} className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${sigMode === 'type' ? 'bg-white shadow text-[#0077B6]' : 'text-gray-600'}`} data-testid="deal-review-sig-type"><Type className="h-3 w-3" /> Type</button>
                  </div>
                </div>
                {sigMode === 'draw' ? (
                  <div>
                    <div className="border border-gray-300 rounded bg-white">
                      <SignatureCanvas ref={sigPadRef} canvasProps={{ width: 700, height: 140, className: 'w-full h-[140px] rounded' }} penColor="#0B3D7A" />
                    </div>
                    <button type="button" onClick={() => sigPadRef.current?.clear()} className="mt-2 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1" data-testid="deal-review-sig-clear"><Eraser className="h-3 w-3" /> Clear</button>
                  </div>
                ) : (
                  <input type="text" value={sigTyped} onChange={(e) => setSigTyped(e.target.value)} placeholder="Type your full name"
                    style={{ fontFamily: '"Great Vibes", "Dancing Script", "Brush Script MT", cursive', color: '#0B3D7A' }}
                    className="w-full px-3 py-4 text-4xl border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]" data-testid="deal-review-sig-typed" />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Compliance Officer Printed Name *</label>
                    <input type="text" value={form.signed_name} onChange={(e) => set('signed_name', e.target.value)} className={inputCls} data-testid="deal-review-field-signed_name" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                    <input type="date" value={form.signature_date} onChange={(e) => set('signature_date', e.target.value)} className={inputCls} data-testid="deal-review-field-signature_date" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="deal-review-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {existing ? 'Update Review & Regenerate PDF' : 'Sign & Generate Review PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DealComplianceReviewModal;
